import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginService } from '@core/authentication';
import { AuthWindowComponent } from '../auth-window/auth-window.component';
import { LoginSettingsService } from '../auth-window/login-settings.service';

/**
 * "Passwort vergessen" and "Link gesendet" (Club-Manager-Login-Spec 4). The confirmation never
 * says whether an account exists for the address.
 */
@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, MatIcon, TranslateModule, AuthWindowComponent],
})
export class ForgotPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly loginService = inject(LoginService);
  private readonly translate = inject(TranslateService);

  readonly settings = toSignal(inject(LoginSettingsService).settings$);

  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  /** Address the link was sent to: switches to the state "Link gesendet". */
  readonly sentTo = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  get invalid(): boolean {
    const email = this.form.controls.email;
    return email.invalid && email.touched;
  }

  send(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const email = this.form.controls.email.value.trim();
    this.submitting.set(true);
    this.error.set(null);
    this.loginService.forgotPassword(email).subscribe({
      next: () => {
        this.submitting.set(false);
        this.sentTo.set(email);
      },
      error: (response: HttpErrorResponse) => {
        this.submitting.set(false);
        this.error.set(
          this.translate.instant(response.status === 422 ? 'auth.error_email' : 'auth.error_generic')
        );
      },
    });
  }
}
